/**
 * @fileoverview Table de routage de la console backoffice GNPC.
 *
 * Utilise `createBrowserRouter` (React Router v6 data APIs) pour bénéficier
 * des futurs `loader` / `action` quand on branchera les endpoints admin.
 *
 * Structure des layouts :
 *   - `ConsoleLayout` — sidebar principale + toutes les routes opérationnelles
 *     (Accueil, futur Dashboard, Payments, Refunds, Audit, Users, Exports).
 *   - `DesignSystemLayout` — sidebar contextuelle avec les sections DS.
 *     Utilisé uniquement pour `/design-system`. La page reste accessible
 *     par URL directe — elle n'apparaît volontairement pas dans la
 *     navigation principale (outil dev/design, pas fonction opérationnelle).
 *
 * Convention d'ajout de route :
 *   1. Créer la page dans `src/pages/<Nom>Page.tsx`.
 *   2. L'ajouter comme enfant du layout approprié ci-dessous.
 *   3. Wrapper d'un `<CapabilityGuard capability="…">` quand la page exige
 *      une capacité backoffice (voir guide frontend §5.2).
 *   4. Mettre à jour la sidebar concernée (avec un `<CapabilityGuard>`
 *      autour de l'item — masquage UX only, la vraie autorisation reste
 *      un 403 côté backend).
 *
 * @see Documents/Paymetrust_Guide_Integration_Frontend_v1.pdf §5
 */

import { Navigate, createBrowserRouter } from 'react-router-dom';
import { CapabilityGuard } from './features/auth/CapabilityProvider.tsx';
import { ConsoleLayout } from './layouts/ConsoleLayout.tsx';
import { DesignSystemLayout } from './layouts/DesignSystemLayout.tsx';
import { AccountMfaPage } from './pages/AccountMfaPage.tsx';
import { AccountProfilePage } from './pages/AccountProfilePage.tsx';
import { AccountSecurityPage } from './pages/AccountSecurityPage.tsx';
import { AuditLogPage } from './pages/AuditLogPage.tsx';
import { DashboardPage } from './pages/DashboardPage.tsx';
import { DesignSystemPage } from './pages/DesignSystemPage.tsx';
import { ExportsPage } from './pages/ExportsPage.tsx';
import { IntegrationsPage } from './pages/IntegrationsPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { PaymentDetailPage } from './pages/PaymentDetailPage.tsx';
import { PaymentsPage } from './pages/PaymentsPage.tsx';
import { ReconciliationPage } from './pages/ReconciliationPage.tsx';
import { RefundsPage } from './pages/RefundsPage.tsx';
import { RolePermissionDetailPage } from './pages/RolePermissionDetailPage.tsx';
import { RolesPermissionsPage } from './pages/RolesPermissionsPage.tsx';
import { UsersPage } from './pages/UsersPage.tsx';

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  [
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/',
      element: <ConsoleLayout />,
      // Filet de sécurité : erreurs de rendu / navigation (dont les 404 hors
      // layout) tombent sur la page brandée plutôt que l'écran technique par
      // défaut de React Router.
      errorElement: <NotFoundPage />,
      children: [
        {
          index: true,
          element: (
            <CapabilityGuard capability="dashboard:read">
              <DashboardPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'payments',
          element: (
            <CapabilityGuard capability="payment:read">
              <PaymentsPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'payments/:paymentId',
          element: (
            <CapabilityGuard capability="payment:read">
              <PaymentDetailPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'refunds',
          element: (
            <CapabilityGuard capability="refund:request">
              <RefundsPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'reconciliation',
          element: (
            <CapabilityGuard capability="reconciliation:read">
              <ReconciliationPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'exports',
          element: (
            <CapabilityGuard capability="export:create">
              <ExportsPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'integrations',
          element: (
            <CapabilityGuard capability="integrations:health">
              <IntegrationsPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'audit',
          element: (
            <CapabilityGuard capability="audit:read">
              <AuditLogPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'users',
          element: (
            <CapabilityGuard capability="user:read">
              <UsersPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'roles-permissions',
          element: (
            <CapabilityGuard capability="roles:manage">
              <RolesPermissionsPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'roles-permissions/:roleId',
          element: (
            <CapabilityGuard capability="roles:manage">
              <RolePermissionDetailPage />
            </CapabilityGuard>
          ),
        },
        {
          path: 'account',
          element: <Navigate to="/account/profile" replace />,
        },
        {
          path: 'account/profile',
          element: <AccountProfilePage />,
        },
        {
          path: 'account/security',
          element: <AccountSecurityPage />,
        },
        {
          path: 'account/mfa',
          element: <AccountMfaPage />,
        },
        {
          // Catch-all 404 DANS le layout : l'agent garde la sidebar et la
          // topbar pour se réorienter sans perdre le contexte console.
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
    {
      path: '/design-system',
      element: <DesignSystemLayout />,
      children: [{ index: true, element: <DesignSystemPage /> }],
    },
  ],
  { basename: '/_' },
);
